/* Parcel Query thread — open / notes with timestamps / follow-up / outcome. Opened via window event "pq-open". */
import { QueryHover, isUnread } from "@/components/QueryHover";
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, getAuthHeader } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { FileChips, AttachButton, PackingPhotos } from "@/components/QueryFiles";

export const openParcelQuery = (rowId) => window.dispatchEvent(new CustomEvent("pq-open", { detail: { rowId } }));

const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "");
const KIND = { open: "Opened", note: "Note", followup: "Follow-up", outcome: "Outcome", reopen: "Reopened", processed: "Processed", partner: "Partner query", rating: "Partner rating" };
const STATE_CLS = { open: "bg-orange-500", due: "bg-red-600", solved: "bg-emerald-600", processed: "bg-blue-600" };
const STATE_TXT = { open: "QUERY OPEN", due: "FOLLOW-UP DUE", solved: "QUERY SOLVED", processed: "PROCESSED" };

export const ParcelQueryBadge = ({ q, rowId }) => (!q ? null : (
  <QueryHover rowId={rowId}>
  <button type="button" onClick={() => openParcelQuery(rowId)}
    className={`ml-1 inline-block rounded text-white text-[9px] font-bold px-1 py-px align-middle ${q.awaiting_team ? "bg-fuchsia-600 animate-pulse" : q.late || q.stale ? "bg-rose-700 animate-pulse" : STATE_CLS[q.state]}`}
    title={undefined}
    data-testid={`or-pquery-badge-${rowId}`}>
    {q.awaiting_team ? "PARTNER QUERY · REPLY" : q.late ? "TO PROCESS 24h+" : q.stale ? "NO REPLY 24h+" : STATE_TXT[q.state]}{q.state === "solved" && q.outcome ? `: ${q.outcome.split(" –")[0]}` : ""}
  </button>
  </QueryHover>
));

const _tplCache = {};
const isAdminUser = () => { try { return JSON.parse(localStorage.getItem("user") || "{}").role === "admin"; } catch { return false; } };

const TemplateChips = ({ onPick, src = "templates", label = "" }) => {
  const [tpls, setTpls] = useState(_tplCache[src] || []);
  const [edit, setEdit] = useState(null);
  const tid = { templates: "pq-template", "tracking-templates": "pq-tracking-template" }[src] || "pq-partner-template";
  useEffect(() => {
    if (_tplCache[src]) return;
    axios.get(`${API}/order-register/queries/${src}`, { headers: getAuthHeader() })
      .then((r) => { _tplCache[src] = r.data.templates; setTpls(_tplCache[src]); }).catch(() => {});
  }, [src]);
  const save = async () => {
    try {
      const r = await axios.put(`${API}/order-register/queries/${src}`, { templates: edit.split("\n") }, { headers: getAuthHeader() });
      _tplCache[src] = r.data.templates; setTpls(_tplCache[src]); setEdit(null); toast.success("Templates saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  if (edit !== null) return (
    <div className="space-y-1" data-testid={`${tid}s-editor`}>
      <Textarea value={edit} onChange={(e) => setEdit(e.target.value)} rows={6} data-testid={`${tid}s-text`} />
      <div className="flex gap-2"><Button size="sm" onClick={save} data-testid={`${tid}s-save`}>Save templates</Button>
        <Button size="sm" variant="ghost" onClick={() => setEdit(null)}>Cancel</Button></div>
      <p className="text-[10px] text-slate-400">One template per line.</p>
    </div>
  );
  return (
    <div className="flex flex-wrap items-center gap-1" data-testid={`${tid}s`}>
      {label && <span className="text-[11px] font-semibold text-fuchsia-700">{label}</span>}
      {tpls.map((t, i) => (
        <button key={`${i}-${t}`} type="button" onClick={() => onPick(t)} data-testid={`${tid}-${i}`}
          className={`rounded-full border px-2 py-0.5 text-[11px] ${src === "templates" ? "border-slate-300 bg-white text-slate-700 hover:bg-orange-50 hover:border-orange-300" : "border-fuchsia-300 bg-fuchsia-50 text-fuchsia-800 hover:bg-fuchsia-100"}`}>{t}</button>
      ))}
      {isAdminUser() && <button type="button" onClick={() => setEdit(tpls.join("\n"))} className="text-[11px] text-indigo-600 underline px-1" data-testid={`${tid}s-edit`}>edit</button>}
    </div>
  );
};

const addTpl = (cur, t) => (cur.trim() ? `${cur.trim()} · ${t}` : t);

const toIso = (local) => (local ? new Date(local).toISOString() : "");

const Thread = ({ q }) => (
  <ol className="space-y-1.5 max-h-64 overflow-auto rounded border bg-slate-50 p-2" data-testid="pq-thread">
    {(q.entries || []).map((e, i) => (
      <li key={i} className={`text-xs ${isUnread(q, e) ? "rounded bg-amber-100 px-1 py-0.5 ring-1 ring-amber-300" : e.kind === "partner" ? "rounded bg-fuchsia-50 px-1 py-0.5" : ""}`} data-testid={`pq-entry-${i}`}>
        {isUnread(q, e) && <span className="mr-1 rounded bg-amber-500 px-1 text-[9px] font-bold text-white" data-testid={`pq-entry-new-${i}`}>NEW</span>}
        <span className="text-slate-500">{fmt(e.at)}, <b className="text-slate-700">{e.by_name}</b> · {KIND[e.kind] || e.kind}:</span>{" "}
        <span className={e.kind === "outcome" ? "font-semibold text-emerald-700" : e.kind === "processed" ? "font-semibold text-blue-700" : e.kind === "partner" ? "font-semibold text-fuchsia-800" : "text-slate-800"}>{e.text}</span>
        <FileChips files={e.files} />
      </li>
    ))}
  </ol>
);

const OpenForm = ({ data, onDone, rowId }) => {
  const [type, setType] = useState(data.types[0]);
  const [prods, setProds] = useState([]);
  const [note, setNote] = useState("");
  const submit = async () => {
    try {
      await axios.post(`${API}/order-register/queries`, { row_id: rowId, type, products: prods, note }, { headers: getAuthHeader() });
      toast.success("Query opened"); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not open query"); }
  };
  return (
    <div className="space-y-2" data-testid="pq-open-form">
      <select value={type} onChange={(e) => setType(e.target.value)} className="h-8 w-full rounded border px-2 text-sm" data-testid="pq-type">
        {data.types.map((t) => <option key={t}>{t}</option>)}
      </select>
      <div className="flex flex-wrap gap-1">
        {data.products.map((p) => (
          <label key={p} className={`text-xs px-2 py-0.5 rounded-full border cursor-pointer ${prods.includes(p) ? "bg-orange-100 border-orange-400" : "bg-white"}`}>
            <input type="checkbox" className="mr-1 align-middle" checked={prods.includes(p)} data-testid={`pq-product-${p}`}
              onChange={() => setProds(prods.includes(p) ? prods.filter((x) => x !== p) : [...prods, p])} />{p}
          </label>
        ))}
      </div>
      <TemplateChips onPick={(t) => setNote((n) => addTpl(n, t))} />
      <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="What is the issue? What should the query team ask the customer?" data-testid="pq-open-note" />
      <Button onClick={submit} disabled={!note.trim()} className="bg-orange-600 hover:bg-orange-700" data-testid="pq-open-submit">Open query</Button>
    </div>
  );
};

const ProcessedBox = ({ q, onDone }) => {
  const [pwd, setPwd] = useState("");
  const [needPwd, setNeedPwd] = useState(false);
  const [note, setNote] = useState("");
  const mark = async () => {
    try {
      await axios.post(`${API}/order-register/queries/${q.id}/processed`, { password: pwd || undefined, note }, { headers: getAuthHeader() });
      toast.success("Marked processed"); onDone();
    } catch (e) {
      const d = e.response?.data?.detail || "Failed";
      if (String(d).startsWith("PQ_LOCKED")) { setNeedPwd(true); if (pwd) toast.error("Wrong password"); else toast.info("Enter your password — stays unlocked 3 hours"); }
      else toast.error(d);
    }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 rounded border border-blue-200 bg-blue-50 p-2" data-testid="pq-processed-box">
      <span className="text-xs text-blue-800">Change done for <b>{q.outcome}</b>?</span>
      <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" className="h-8 flex-1 min-w-[140px]" data-testid="pq-processed-note" />
      {needPwd && <Input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="Your login password" className="h-8 w-full sm:w-48" data-testid="pq-processed-password" />}
      <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={mark} data-testid="pq-processed-btn">Mark Processed</Button>
    </div>
  );
};

const isQueryTeam = () => { try { return !!JSON.parse(localStorage.getItem("user") || "{}").is_query_team; } catch { return false; } };

const Actions = ({ q, data, onDone }) => {
  const [text, setText] = useState("");
  const [fu, setFu] = useState("");
  const [outcome, setOutcome] = useState(data.outcomes[0]);
  const post = async (path, body, msg) => {
    try { await axios.post(`${API}/order-register/queries/${q.id}/${path}`, body, { headers: getAuthHeader() }); toast.success(msg); setText(""); setFu(""); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  if (q.status === "solved") {
    return (
      <div className="space-y-2">
        {!q.processed_at && !isQueryTeam() && <ProcessedBox q={q} onDone={onDone} />}
        {q.processed_at && <p className="text-xs font-semibold text-blue-700" data-testid="pq-processed-info">Processed by {q.processed_by_name}, {fmt(q.processed_at)}</p>}
      <div className="flex gap-2 items-center">
        <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Why reopen? (optional)" data-testid="pq-reopen-text" />
        <Button variant="outline" onClick={() => post("reopen", { text }, "Query reopened")} data-testid="pq-reopen-btn">Reopen</Button>
      </div>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {q.tracking_concern && <TemplateChips src="tracking-templates" label="Tracking reply:" onPick={(t) => setText((n) => addTpl(n, t))} />}
      {q.from_partner && <TemplateChips src="partner-templates" label="Reply to partner:" onPick={(t) => setText((n) => addTpl(n, t))} />}
      <TemplateChips onPick={(t) => setText((n) => addTpl(n, t))} />
      <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder={q.from_partner ? "Reply to the partner (they see this note)" : "Customer contact note (e.g. Called, asked to replace X with Y)"} data-testid="pq-note-text" />
      <div className="flex flex-wrap items-center gap-2">
        <label className="text-xs text-slate-600">Next follow-up</label>
        <Input type="datetime-local" value={fu} onChange={(e) => setFu(e.target.value)} className="h-8 w-full sm:w-56" data-testid="pq-followup-at" />
        <Button size="sm" disabled={!text.trim()} onClick={() => post("note", { text, next_followup_at: toIso(fu) || undefined }, "Note added")} data-testid="pq-note-submit">Add note</Button>
        <AttachButton qid={q.id} text={text} onDone={() => { setText(""); onDone(); }} />
        <Button size="sm" variant="outline" disabled={!fu} onClick={() => post("followup", { at: toIso(fu) }, "Follow-up set")} data-testid="pq-followup-submit">Set follow-up only</Button>
      </div>
      <div className="flex flex-wrap items-center gap-2 border-t pt-2">
        <label className="text-xs font-semibold text-emerald-700">Outcome</label>
        <select value={outcome} onChange={(e) => setOutcome(e.target.value)} className="h-8 rounded border px-2 text-sm" data-testid="pq-outcome">
          {data.outcomes.map((o) => <option key={o}>{o}</option>)}
        </select>
        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => post("outcome", { outcome, note: text }, "Query solved")} data-testid="pq-solve-btn">Solve query</Button>
      </div>
    </div>
  );
};

export const ParcelQueryDialog = ({ onChanged }) => {
  const [rowId, setRowId] = useState(null);
  const [data, setData] = useState(null);
  const load = useCallback(async (id) => {
    try { const r = await axios.get(`${API}/order-register/queries/by-row/${id}`, { headers: getAuthHeader() }); setData(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to load query"); }
  }, []);
  useEffect(() => {
    const h = (e) => { setRowId(e.detail.rowId); setData(null); load(e.detail.rowId); };
    window.addEventListener("pq-open", h);
    const qid = new URLSearchParams(window.location.search).get("query");  // bell deep link
    if (qid) axios.get(`${API}/order-register/queries/one/${qid}`, { headers: getAuthHeader() })
      .then((r) => r.data.row_ids?.[0] && openParcelQuery(r.data.row_ids[0])).catch(() => {})
      .finally(() => { const u = new URL(window.location.href); u.searchParams.delete("query"); window.history.replaceState(null, "", u); });
    return () => window.removeEventListener("pq-open", h);
  }, [load]);
  const done = () => { load(rowId); onChanged?.(); window.dispatchEvent(new Event("pq-changed")); };
  const q = data?.current;
  return (
    <Dialog open={!!rowId} onOpenChange={(o) => !o && setRowId(null)}>
      <DialogContent className="w-[96vw] max-w-2xl max-h-[90vh] overflow-y-auto p-4 sm:p-6" data-testid="pq-dialog">
        <DialogHeader><DialogTitle>Parcel query {q && <span className={`ml-2 rounded text-white text-[10px] px-1.5 py-0.5 ${STATE_CLS[q.state]}`}>{STATE_TXT[q.state]}</span>}</DialogTitle></DialogHeader>
        {!data ? <p className="text-sm text-slate-400">Loading…</p> : (
          <div className="space-y-3">
            <PackingPhotos rowId={rowId} onDone={done} />
            {q ? (
              <>
                <p className="text-xs text-slate-600" data-testid="pq-summary">
                  <b>{q.customer_name}</b> · {q.code} · {q.date} · {q.mode} — <b className="text-orange-700">{q.type}</b>{q.concern_topic && <span className="ml-1 rounded bg-rose-100 px-1.5 py-0.5 text-[11px] font-semibold text-rose-700" data-testid="pq-concern-topic">{q.concern_topic}</span>}
                  {q.products?.length ? ` · ${q.products.join(", ")}` : ""}
                  {q.next_followup_at && q.status === "open" && <span className={q.state === "due" ? "text-red-600 font-semibold" : ""}> · follow-up {fmt(q.next_followup_at)}</span>}
                  {q.outcome && <span className="text-emerald-700 font-semibold"> · {q.outcome}</span>}
                </p>
                <Thread q={q} />
                <Actions q={q} data={data} onDone={done} />
              </>
            ) : <OpenForm data={data} rowId={rowId} onDone={done} />}
            {data.history.length > 1 && <p className="text-[11px] text-slate-400">{data.history.length - 1} earlier query thread(s) on this parcel</p>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
